import { Dispatch, ReactNode } from 'react';
import { IDialog } from '@honeybook/hbui/dist/components/HBDialog/HBDialog';
import type {
    IEmailAttachment,
    IEmailSignature,
    ISendTime
} from '~/types/model.types';
import { TCKEditor } from '~/components/common/CKEditorWrapper/ckeditor.types';
import type { TEmailType } from '~/types/server.types';
import { TOneComposerSmartFields } from '~/common/SmartFields/smart-fields.types';
import { ISplitButtonProps } from '@honeybook/hbui/dist/components/HBButton/HBSplitButton';
import { IModal } from '@honeybook/hbui/dist/components/HBModal/HBModal';
import { TOneComposerModalCloseSource } from '~/components/OneComposer/OneComposerModal/OneComposerModal.types';
import type { Flow } from '~/models/flow.model';
import type { User } from '~/models/user.model';
import type { Company } from '~/models/company.model';
import type { ToastStore } from '~/stores/toast.store';
import { EnforceValidKeys } from '~/types/app.types';

export interface IOneComposerProps {
    /** adds the ability to schedule send the email (requires appropriate server api). */
    allowScheduledSend?: boolean;
    /** used for many things, like brand color for default link color and custom cta button, displayed in the unsubscribe footer preview etc... */
    currCompany: Company;
    /** used for sender details, email education, referrals, analytics, etc... */
    currUser: User;
    /** used for StyledComponents to extend styles of OneComposerMainContainer. */
    className?: string;
    /** the text to be shown at the main CTA button. if not provided will show default depending on editor send mode: regular/test/scheduled. */
    ctaText?: string;
    /** shows custom recipients row text. */
    customRecipientsRowText?: string;
    /** will not warn if there are no recipients for the email. defaults to true */
    disableRecipientCheck?: boolean;
    /** specify the editor type for error/analytics purposes and other. */
    editorType: OneComposerEditorType;
    /** needed for adding signature/showing signature. also used for preview. */
    emailSignature?: IEmailSignature;
    /** smart file instance that is requires for related actions. */
    flow?: Flow;
    /** hide the bottom toolbar, will also hide the format toolbar. needed for disabled/preview/non-editable reasons. */
    hideBottomToolbar?: boolean;
    /** run functions and show loader until all promises are resolved */
    initializeWithPromises?: Array<() => Promise<any>>;
    /** external disable contorl prop. will disable all editing/toolbar functions. useful during loading, sending or preview purposes. */
    isDisabledFromProp?: boolean;
    /** editor is sending in test mode. will show "SEND TEST" cta, disable scheduled send functions, show suitable sending status modal, shows different variant at the recipient row, disables adding recipients, and adds "[TEST]" to email subject. */
    isSendTest?: boolean;
    /** used for adapting subject, body and recipient validations and bypass smartfields use rules for templating. */
    isTemplateEditor?: boolean;
    /** the logo url to be used in BE-supported email templates. */
    logoUrl?: string | null;
    /** specify minimum height for editor, default is 190px. */
    minEditorContentHeight?: number;
    /** Add multi participants via input support. */
    isWithRecipientsInput?: boolean;
    /** function to close the modal. provides close source parameter. Relevant only when using through OneComposerModal. */
    onCloseModal?: IOneComposerModalProps['onCloseModal'];
    /** the internal composer state change handler function, provided from useOneComposer. */
    oneComposerDispatch: Dispatch<TOneComposerAction>;
    /** the internal composer state, provided from useOneComposer. */
    oneComposerState: TOneComposerState;
    /** callback to open the add participant modal. it'll allow to add participants to the workspace by clicking on the recipient row plus icon. */
    openAddParticipantModal?: () => Promise<IOneComposerRecipient>;
    /** sender details. to override use from currUser. */
    senderDetails?: IOneComposerSenderDetails;
    /** if provided, will use split menu on cta button, where you can add more functions. use interface of ISplitButtonProps['splitButtonProps']. */
    splitButtonProps?: ISplitButtonProps['splitButtonProps'];
    /** toast store to trigger toasts.  */
    toastStore: ToastStore;
    /** used internally to signal it is rendered using OneComposerModal. */
    _isInModal?: boolean;
}

export type TOneComposerCTAClick = ({
    oneComposerDispatch,
    oneComposerState,
    serverFormatHtmlBody,
    serverFormatSubject
}: {
    oneComposerDispatch: Dispatch<TOneComposerAction>;
    oneComposerState: TOneComposerState;
    serverFormatHtmlBody: string;
    serverFormatSubject: string;
}) => Promise<TOneComposerSendingResult>;

export interface IOneComposerModalProps
    extends IOneComposerProps,
        Pick<
            IModal,
            | 'actions'
            | 'closeButtonTooltipText'
            | 'isChildModal'
            | 'isOpen'
            | 'onBackdropClick'
            | 'title'
            | 'testId'
            | 'maxWidth'
            | 'bodyStyles'
            | 'hasChildModalOverlay'
            | 'className'
        > {
    onCloseModal: (props?: { source?: TOneComposerModalCloseSource }) => void;
}

export enum OneComposerEditorType {
    AutomationPendingTaskEmailEditor = 'automationPendingTaskEmailEditor',
    NewAutomationsSendEmailAction = 'NewAutomationsSendEmailAction',
    SuggestedEmailComposerModal = 'suggestedEmailComposerModal',
    BatchEmailEditor = 'batchEmailEditor',
    BulkContactsModal = 'bulkContactsModal',
    BulkWorkspacesModal = 'bulkWorkspacesModal',
    ClientPortalEmailComposer = 'clientPortalEmailComposer',
    ContactViewEditor = 'contactViewEditor',
    ContactViewProjectEditor = 'contactViewProjectEditor',
    EmailTemplateEditor = 'emailTemplateEditor',
    FeedMessageEditor = 'feedMessageEditor',
    LeadFormMessageEditor = 'leadFormMessageEditor',
    MalkutDisputeReminderModal = 'malkutDisputeReminderModal', // opened from OneComposerModalAngularWrapper
    MalkutRequestDocumentsModal = 'malkutRequestDocumentsModal', // opened from OneComposerModalAngularWrapper
    PublishChangesModal = 'publishChangesModal',
    LegacyRequestMilestonePaymentModal = 'legacyRequestMilestonePaymentModal', // opened from OneComposerModalAngularWrapper
    OOOMessageEditor = 'oooMessageEditor',
    RequestProposalModal = 'requestProposalModal', // deprecated opened from OneComposerModalAngularWrapper
    RescheduleProjectModal = 'rescheduleProjectModal', // opened from OneComposerModalAngularWrapper
    RequestPayment = 'requestPayment',
    SendClientPortalLinkWorkspaceModal = 'sendClientPortalLinkWorkspaceModal', // opened from OneComposerModalAngularWrapper
    SendClientPortalLinkSettingsModal = 'sendClientPortalLinkSettingsModal', // opened from OneComposerModalAngularWrapper
    SendLegacyFileModal = 'sendLegacyFileModal', // opened from OneComposerModalAngularWrapper
    SendSMAFTest = 'sendSMAFTest',
    ShareSmaf = 'shareSmaf',
    StopRecurring = 'stopRecurring',
    TaskEmailModal = 'taskEmailModal', // opened from OneComposerModalAngularWrapper
    SessionReminderModal = 'SessionReminderModal',
    VendorReferralModal = 'vendorReferralModal', // opened from OneComposerModalAngularWrapper
    ShareReferral = 'shareReferral',
    WorkflowEmailModal = 'workflowEmailModal' // deprecated
}

interface IOneComposerSenderDetails {
    company_id: string;
    email: string;
    first_name?: string;
    full_name?: string;
}

export interface IOneComposerRecipient {
    _id?: string;
    email?: string;
    first_name?: string;
    full_name?: string;
    is_unsubscribed?: boolean;
}

export interface IOneComposerEmail {
    _id?: string;
    email_type?: TEmailType;
    file_attachments?: IEmailAttachment[];
    html_body: string;
    image_attachments?: IEmailAttachment[];
    subject: string;
    time_to_send?: Partial<ISendTime>; // removal Partial once all editors move to one composer
    title?: string;
    suggestion_id?: string;
}

export type TOneComposerState = {
    bodyEditorInstance: null | TCKEditor;
    dialogProps:
        | (IDialog & { isOpen: false })
        | (IDialog & { isOpen: true; testId: string });
    disableAttachments: boolean;
    email: IOneComposerEmail;
    errorMessage: { message: ReactNode; timeoutInSeconds?: number };
    focusedEditor: 'subject' | 'body' | null;
    formatToolbarBottomPosition?: number;
    isAttachmentsUploading: boolean;
    isChildModalOpen: boolean;
    shouldShowAITooltipOnInit: boolean;
    isFormatToolbarShown: boolean;
    isInvalidScheduleDate: boolean;
    isLoading: boolean;
    isLogoShown: boolean;
    isReplyMode: boolean;
    isScheduledSendPanelOpen: boolean;
    isSignatureShown: boolean;
    isCommercialEmail: boolean;
    recipients: IOneComposerRecipient[];
    resultDialogProps: IResultDialogProps;
    scheduledToBeSendDate: string;
    sendingStatus: OneComposerSendingStatus;
    smartFields: null | TOneComposerSmartFields;
    subjectEditorInstance: null | TCKEditor;
};

export enum OneComposerSendingStatus {
    Idle = 'idle',
    IsSending = 'isSending',
    IsSendingBulk = 'isSendingBulk',
    IsSendingSmaf = 'isSendingSmaf',
    IsSendingTest = 'isSendingTest',
    IsSendingScheduled = 'isSendingScheduled',
    SuccessScheduled = 'successScheduled',
    SuccessSent = 'successSent',
    SuccessSentTest = 'successSentTest',
    SentWithFailure = 'sentWithFailure',
    Failure = 'failure'
}

export type TOneComposerSendingResult = {
    errorMessage?: TOneComposerState['errorMessage'];
    status: OneComposerSendingStatus;
} & IResultDialogProps;

interface IResultDialogProps {
    ctaText?: string;
    ctaAction?: () => void;
    emailList?: string[];
    icon?: JSX.Element;
    description?: string;
    title?: string;
}

export type TOneComposerActionCallback = ({
    action,
    newState
}: {
    action: TOneComposerAction;
    newState: TOneComposerState;
}) => void;

export enum OneComposerAction {
    AddRecipient = 'AddRecipient',
    CancelScheduleSend = 'CancelScheduleSend',
    ChangeTemplate = 'ChangeTemplate',
    DeleteRecipient = 'DeleteRecipient',
    RemoveAttachment = 'RemoveAttachment',
    Reset = 'Reset',
    SetDialogProps = 'SetDialogProps',
    SetEditorInstance = 'SetEditorInstance',
    SetErrorMessage = 'SetErrorMessage',
    SetFocusedEditor = 'SetFocusedEditor',
    SetSendingResult = 'SetSendingResult',
    SetSendingStatus = 'SetSendingStatus',
    ToggleCommercialEmail = 'ToggleCommercialEmail',
    ToggleOneComposerState = 'ToggleOneComposerState',
    ToggleReplyMode = 'ToggleReplyMode',
    ToggleSignature = 'ToggleSignature',
    UpdateAttachments = 'UpdateAttachments',
    UpdateEmail = 'UpdateEmail',
    UpdateRecipients = 'UpdateRecipients',
    UpdateSmartFields = 'UpdateSmartFields',
    UpdateState = 'UpdateState',
    UpdateTimeToSend = 'UpdateTimeToSend'
}

export type TOneComposerAction =
    | IAddRecipientAction
    | ICancelScheduleSendAction
    | IChangeTemplateAction
    | IDeleteRecipientAction
    | IRemoveAttachmentAction
    | IResetAction
    | ISetDialogPropsAction
    | ISetEditorInstanceAction
    | ISetErrorMessageAction
    | ISetFocusedEditorAction
    | ISetSendingStatusAction
    | ISetSendingResultAction
    | IToggleOneComposerStateAction
    | IToggleReplyModeAction
    | IToggleSignatureAction
    | IToggleCommercialEmailAction
    | IUpdateAttachmentsAction
    | IUpdateEmailAction
    | IUpdateRecipientsAction
    | IUpdateSmartFieldsAction
    | IUpdateStateAction
    | IUpdateTimeToSendAction;

// extract only the union members of TOneComposerAction that have a payload attribute
export type TOneComposerActionWithPayload = Extract<
    TOneComposerAction,
    { payload: any }
>;

// See: https://stackoverflow.com/questions/74321882/union-of-interfaces-with-a-type-field-create-an-object-typed-as-type-specifi
// for an explanation of the following type
export type TOneComposerActionToPayloadMap = {
    [T in TOneComposerActionWithPayload as T['type']]: (
        opts?: Partial<T['payload']>
    ) => {
        type: T['type'];
        payload: T['payload'];
    };
};

interface IAddRecipientAction {
    type: OneComposerAction.AddRecipient;
    payload: IOneComposerRecipient;
}

interface ICancelScheduleSendAction {
    type: OneComposerAction.CancelScheduleSend;
}

interface IChangeTemplateAction {
    type: OneComposerAction.ChangeTemplate;
    payload: TOneComposerState['email'];
}

interface IDeleteRecipientAction {
    type: OneComposerAction.DeleteRecipient;
    payload: { recipientId: string };
}

interface IRemoveAttachmentAction {
    type: OneComposerAction.RemoveAttachment;
    payload: { attachmentId: string };
}

interface IResetAction {
    type: OneComposerAction.Reset;
}

interface ISetDialogPropsAction {
    type: OneComposerAction.SetDialogProps;
    payload: TOneComposerState['dialogProps'];
}
interface ISetEditorInstanceAction {
    type: OneComposerAction.SetEditorInstance;
    payload: {
        editorKey: EnforceValidKeys<
            TOneComposerState,
            'bodyEditorInstance' | 'subjectEditorInstance'
        >;
        editorInstance: TCKEditor;
    };
}

interface ISetErrorMessageAction {
    type: OneComposerAction.SetErrorMessage;
    payload: TOneComposerState['errorMessage'];
}

interface ISetSendingStatusAction {
    type: OneComposerAction.SetSendingStatus;
    payload: TOneComposerState['sendingStatus'];
}

interface ISetSendingResultAction {
    type: OneComposerAction.SetSendingResult;
    payload: TOneComposerSendingResult;
}

export type TOneComposerToggleableState = EnforceValidKeys<
    TOneComposerState,
    | 'isAttachmentsUploading'
    | 'isChildModalOpen'
    | 'isFormatToolbarShown'
    | 'isInvalidScheduleDate'
    | 'shouldShowAITooltipOnInit'
    | 'isLoading'
    | 'isLogoShown'
    | 'isScheduledSendPanelOpen'
>;

interface IToggleOneComposerStateAction {
    type: OneComposerAction.ToggleOneComposerState;
    payload: {
        key: keyof Pick<TOneComposerState, TOneComposerToggleableState>;
        value?: boolean;
    };
}

interface IToggleReplyModeAction {
    type: OneComposerAction.ToggleReplyMode;
    payload: {
        isReplyMode?: boolean;
        lastMessageSubject: string;
    };
}

interface IToggleSignatureAction {
    type: OneComposerAction.ToggleSignature;
    payload: boolean;
}

interface IToggleCommercialEmailAction {
    type: OneComposerAction.ToggleCommercialEmail;
    payload: boolean;
}

interface IUpdateAttachmentsAction {
    type: OneComposerAction.UpdateAttachments;
    payload: IEmailAttachment[];
}

interface IUpdateEmailAction {
    type: OneComposerAction.UpdateEmail;
    payload: {
        email: Partial<TOneComposerState['email']>;
        isExternalUpdate?: boolean;
    };
}

interface IUpdateRecipientsAction {
    type: OneComposerAction.UpdateRecipients;
    payload: IOneComposerRecipient[];
}

interface IUpdateSmartFieldsAction {
    type: OneComposerAction.UpdateSmartFields;
    payload: TOneComposerSmartFields;
}

interface IUpdateStateAction {
    type: OneComposerAction.UpdateState;
    payload: Partial<TOneComposerState>;
}

interface IUpdateTimeToSendAction {
    type: OneComposerAction.UpdateTimeToSend;
    payload: TOneComposerState['email']['time_to_send'];
}

interface ISetFocusedEditorAction {
    type: OneComposerAction.SetFocusedEditor;
    payload: TOneComposerState['focusedEditor'];
}
